import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Snackbar,
  TextField,
  Typography
} from '@mui/material';
import {
  Add,
  CheckCircle,
  Close,
  Remove,
  Restaurant,
  Storefront
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || 'https://all-delivery.onrender.com/api';
const API_ORIGIN = API_URL.replace(/\/api\/?$/, '');
const publicApi = axios.create({ baseURL: API_URL, timeout: 30000 });

const money = (value) => `R$ ${(Number(value) || 0).toFixed(2)}`;
const getSizeOptions = (product) => [
  Number(product?.price_p) > 0 ? { label: 'P', price: Number(product.price_p) } : null,
  Number(product?.price_g) > 0 ? { label: 'G', price: Number(product.price_g) } : null
].filter(Boolean);
const getProductPrice = (product, size = null) => Number(size?.price) || getSizeOptions(product)[0]?.price || Number(product?.price) || 0;
const productPriceLabel = (product) => {
  const sizes = getSizeOptions(product);
  return sizes.length ? sizes.map((size) => `${size.label}: ${money(size.price)}`).join(' / ') : money(product?.price);
};
const assetUrl = (path) => {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;
  return `${API_ORIGIN}${path.startsWith('/') ? path : `/${path}`}`;
};

export default function PublicMesaOrder() {
  const { token } = useParams();
  const [mesa, setMesa] = useState(null);
  const [lanchonete, setLanchonete] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [category, setCategory] = useState('todos');
  const [optionGroups, setOptionGroups] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [pizzaMode, setPizzaMode] = useState('whole');
  const [secondPizzaFlavor, setSecondPizzaFlavor] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [itemObservation, setItemObservation] = useState('');
  const [items, setItems] = useState([]);
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [orderObservation, setOrderObservation] = useState('');
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(null);
  const [message, setMessage] = useState('');

  useEffect(() => {
    async function loadPage() {
      try {
        setLoading(true);
        const mesaResponse = await publicApi.get(`/mesas/public/${token}`);
        const mesaData = mesaResponse.data;
        const [storeResponse, productsResponse] = await Promise.all([
          publicApi.get(`/lanchonetes/${mesaData.lanchonete_id}`),
          publicApi.get('/produtos', {
            params: { lanchonete_id: mesaData.lanchonete_id, available: true }
          })
        ]);
        setMesa(mesaData);
        setLanchonete(storeResponse.data);
        setProducts(Array.isArray(productsResponse.data) ? productsResponse.data : []);
      } catch (requestError) {
        setError(requestError.response?.data?.error || 'Nao foi possivel abrir o cardapio desta mesa');
      } finally {
        setLoading(false);
      }
    }
    loadPage();
  }, [token]);

  const total = useMemo(
    () => items.reduce((sum, item) => sum + item.unitPrice * item.quantity, 0),
    [items]
  );
  const totalQuantity = useMemo(
    () => items.reduce((sum, item) => sum + item.quantity, 0),
    [items]
  );

  const openProduct = async (product) => {
    try {
      setSelectedProduct(product);
      setSelectedOptions({});
      setPizzaMode('whole');
      setSecondPizzaFlavor(null);
      setSelectedSize(getSizeOptions(product)[0] || null);
      setQuantity(1);
      setItemObservation('');
      const response = await publicApi.get(`/product-options/groups/product/${product.id}`);
      setOptionGroups(Array.isArray(response.data) ? response.data : []);
    } catch (_) {
      setOptionGroups([]);
    }
  };

  const toggleOption = (group, option) => {
    setSelectedOptions((current) => {
      const chosen = current[group.id] || [];
      const exists = chosen.some((entry) => Number(entry.id) === Number(option.id));
      const maximum = Number(group.max_selections || (group.type === 'single' ? 1 : 99));
      let next;
      if (exists) next = chosen.filter((entry) => Number(entry.id) !== Number(option.id));
      else if (maximum === 1) next = [option];
      else if (chosen.length < maximum) next = [...chosen, option];
      else next = chosen;
      return { ...current, [group.id]: next };
    });
  };

  const addItem = () => {
    if (getSizeOptions(selectedProduct).length > 0 && !selectedSize) {
      setMessage('Escolha o tamanho da pizza');
      return;
    }
    if (pizzaMode === 'half' && !secondPizzaFlavor) {
      setMessage('Escolha o segundo sabor da pizza');
      return;
    }
    const normalizedType = `${selectedProduct?.name || ''} ${selectedProduct?.category || selectedProduct?.categoria_name || ''}`
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    const hasAvailableOptions = optionGroups.some((group) => Array.isArray(group.options) && group.options.length > 0);
    const needsAtLeastOneOption = hasAvailableOptions && (normalizedType.includes('acai') || normalizedType.includes('sorvete'));
    const selectedCount = Object.values(selectedOptions).reduce((sum, list) => sum + (Array.isArray(list) ? list.length : 0), 0);
    if (needsAtLeastOneOption && selectedCount === 0) {
      setMessage(`Selecione pelo menos uma opcao para ${selectedProduct?.name || 'este produto'}`);
      return;
    }
    for (const group of optionGroups) {
      const chosen = selectedOptions[group.id] || [];
      const minimum = group.required ? Number(group.min_selections || 1) : Number(group.min_selections || 0);
      if (chosen.length < minimum) {
        setMessage(`Escolha as opcoes obrigatorias de ${group.name}`);
        return;
      }
    }

    const options = optionGroups.flatMap((group) => selectedOptions[group.id] || []);
    const secondFlavorSize = selectedSize
      ? getSizeOptions(secondPizzaFlavor).find((size) => size.label === selectedSize.label)
      : null;
    const productPrice = pizzaMode === 'half' && secondPizzaFlavor
      ? Math.max(getProductPrice(selectedProduct, selectedSize), getProductPrice(secondPizzaFlavor, secondFlavorSize))
      : getProductPrice(selectedProduct, selectedSize);
    const unitPrice = productPrice +
      options.reduce((sum, option) => sum + Number(option.price || 0), 0);
    const itemName = pizzaMode === 'half' && secondPizzaFlavor
      ? `${selectedProduct.name} / ${secondPizzaFlavor.name} (metade/metade)`
      : selectedProduct.name;
    setItems((current) => [...current, {
      lineId: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      produto_id: selectedProduct.id,
      name: itemName,
      quantity,
      unitPrice,
      selectedOptions: options,
      observation: itemObservation.trim(),
      pizzaMode,
      secondFlavorId: secondPizzaFlavor?.id || null,
      sizeLabel: selectedSize?.label || null
    }]);
    setSelectedProduct(null);
    setMessage('Produto adicionado ao pedido');
  };

  const removeItem = (lineId) => {
    setItems((current) => current.filter((item) => item.lineId !== lineId));
  };

  const submitOrder = async () => {
    if (!customerName.trim()) {
      setMessage('Informe seu nome');
      return;
    }
    if (items.length === 0) {
      setMessage('Escolha pelo menos um produto');
      return;
    }

    try {
      setSending(true);
      const idempotencyKey = `qr-${token}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const response = await publicApi.post(`/mesas/public/${token}/order`, {
        customer_name: customerName.trim(),
        observation: orderObservation.trim(),
        items: items.map((item) => ({
          produto_id: item.produto_id,
          quantity: item.quantity,
          selected_options: item.selectedOptions.map((option) => option.id),
          observation: item.observation,
          pizza_mode: item.pizzaMode,
          second_flavor_id: item.secondFlavorId,
          size_label: item.sizeLabel
        }))
      }, {
        headers: { 'Idempotency-Key': idempotencyKey }
      });
      setSuccess(response.data);
      setItems([]);
      setOrderDialogOpen(false);
    } catch (requestError) {
      setMessage(requestError.response?.data?.error || 'Nao foi possivel enviar o pedido');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>;
  }

  if (error) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', p: 3, bgcolor: '#f8fafc' }}>
        <Paper sx={{ p: 4, maxWidth: 440, textAlign: 'center', borderRadius: 4 }}>
          <Restaurant sx={{ fontSize: 60, color: 'error.main' }} />
          <Typography variant="h5" fontWeight={900} my={2}>Mesa indisponivel</Typography>
          <Alert severity="error">{error}</Alert>
        </Paper>
      </Box>
    );
  }

  if (success) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', p: 3, bgcolor: '#f0fdf4' }}>
        <Paper sx={{ p: 4, width: '100%', maxWidth: 460, textAlign: 'center', borderRadius: 4 }}>
          <CheckCircle sx={{ fontSize: 78, color: 'success.main' }} />
          <Typography variant="h4" fontWeight={900} mt={2}>Pedido enviado!</Typography>
          <Typography variant="h6" color="primary" mt={1}>Mesa {mesa.mesa_numero}</Typography>
          <Typography color="text.secondary" my={2}>
            Seu pedido #{success.pedido?.lanchonete_order_number || success.pedido?.id} foi enviado para a cozinha.
          </Typography>
          <Button variant="contained" fullWidth onClick={() => setSuccess(null)}>Fazer outro pedido</Button>
        </Paper>
      </Box>
    );
  }

  const categories = ['todos', ...new Set(products.map((product) => product.categoria_name || product.category).filter(Boolean))];
  const filteredProducts = category === 'todos'
    ? products
    : products.filter((product) => String(product.categoria_name || product.category) === String(category));
  const normalizedCategory = String(selectedProduct?.categoria_name || selectedProduct?.category || '')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const isPizzaProduct = String(lanchonete?.type || '').toLowerCase() === 'pizzaria' && normalizedCategory.includes('pizza');
  const secondFlavorOptions = isPizzaProduct
    ? products.filter((product) =>
        Number(product.id) !== Number(selectedProduct?.id) &&
        product.available !== false &&
        String(product.categoria_name || product.category || '') === String(selectedProduct?.categoria_name || selectedProduct?.category || '')
      )
    : [];
  const selectedSecondFlavorSize = selectedSize
    ? getSizeOptions(secondPizzaFlavor).find((size) => size.label === selectedSize.label)
    : null;
  const displayedProductPrice = pizzaMode === 'half' && secondPizzaFlavor
    ? Math.max(getProductPrice(selectedProduct, selectedSize), getProductPrice(secondPizzaFlavor, selectedSecondFlavorSize))
    : getProductPrice(selectedProduct, selectedSize);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', pb: items.length ? 12 : 3 }}>
      <Box sx={{
        height: { xs: 230, sm: 280 },
        position: 'relative',
        overflow: 'hidden',
        backgroundImage: lanchonete?.image
          ? `linear-gradient(rgba(15, 23, 42, .12), rgba(15, 23, 42, .5)), url("${assetUrl(lanchonete.image)}")`
          : 'linear-gradient(135deg, #166534 0%, #16a34a 55%, #f97316 140%)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        boxShadow: '0 10px 28px rgba(15, 23, 42, .18)'
      }}>
        <Chip
          label="Aberto"
          color="success"
          sx={{ position: 'absolute', top: 18, right: 18, color: '#fff', fontWeight: 900, boxShadow: 3 }}
        />
        <Chip
          label={`Mesa ${mesa?.mesa_numero}`}
          sx={{ position: 'absolute', left: 18, bottom: 18, bgcolor: 'rgba(15,23,42,.78)', color: '#fff', fontWeight: 900 }}
        />
      </Box>

      <Box sx={{ maxWidth: 900, mx: 'auto', px: 2, position: 'relative' }}>
        <Avatar
          src={assetUrl(lanchonete?.image)}
          sx={{
            width: 94,
            height: 94,
            mx: 'auto',
            mt: '-47px',
            mb: 1,
            border: '5px solid white',
            boxShadow: '0 8px 24px rgba(15,23,42,.22)',
            bgcolor: '#fff'
          }}
        >
          <Storefront />
        </Avatar>
        <Box textAlign="center">
          <Typography variant="h5" fontWeight={900}>{lanchonete?.name}</Typography>
          <Typography color="text.secondary" mt={0.5}>
            {lanchonete?.description || 'Escolha seus produtos favoritos e peca direto da mesa'}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 1, mt: 2 }}>
            <Chip
              size="small"
              label={lanchonete?.opening_hours || 'Atendimento na mesa'}
              sx={{ bgcolor: '#e2e8f0', fontWeight: 700 }}
            />
            <Chip size="small" label={`Mesa ${mesa?.mesa_numero}`} color="success" sx={{ fontWeight: 900 }} />
            <Chip size="small" label="Sem taxa de entrega" color="success" variant="outlined" sx={{ fontWeight: 700 }} />
          </Box>
        </Box>
      </Box>

      <Box sx={{ maxWidth: 900, mx: 'auto', p: 2, mt: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Box>
            <Typography variant="h5" fontWeight={900}>
              {category === 'todos' ? 'Destaques' : category}
            </Typography>
            <Typography variant="body2" color="text.secondary">Toque em um produto para escolher as opcoes</Typography>
          </Box>
          <Avatar sx={{ width: 38, height: 38, bgcolor: 'primary.main', fontSize: 15, fontWeight: 900 }}>
            {filteredProducts.length}
          </Avatar>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 2, mx: -0.5, px: 0.5 }}>
          {categories.map((item) => (
            <Chip
              key={item}
              label={item === 'todos' ? 'Todos' : item}
              color={category === item ? 'success' : 'default'}
              variant={category === item ? 'filled' : 'outlined'}
              onClick={() => setCategory(item)}
              sx={{ flexShrink: 0, fontWeight: 800 }}
            />
          ))}
        </Box>
        <Box sx={{ display: 'grid', gap: 1.5 }}>
          {filteredProducts.map((product) => (
            <Paper
              key={product.id}
              onClick={() => openProduct(product)}
              sx={{
                p: 0,
                display: 'flex',
                gap: 0,
                alignItems: 'center',
                minHeight: 126,
                overflow: 'hidden',
                borderRadius: 4,
                cursor: 'pointer',
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: '0 8px 22px rgba(15, 23, 42, .08)',
                transition: 'transform .15s ease, box-shadow .15s ease',
                '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 12px 28px rgba(15, 23, 42, .12)' },
                '&:active': { transform: 'scale(.985)' }
              }}
            >
              <Box
                component="img"
                src={assetUrl(product.image)}
                alt={product.name}
                sx={{ width: { xs: 120, sm: 150 }, alignSelf: 'stretch', objectFit: 'cover', bgcolor: '#e5e7eb' }}
              />
              <Box sx={{ flex: 1, minWidth: 0, p: 2, alignSelf: 'stretch', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'flex-start' }}>
                  <Typography fontWeight={900} color="success.main">{productPriceLabel(product)}</Typography>
                  <IconButton size="small" sx={{ bgcolor: 'success.main', color: '#fff', '&:hover': { bgcolor: 'success.dark' } }}>
                    <Add fontSize="small" />
                  </IconButton>
                </Box>
                <Typography fontWeight={900} mt={0.5}>{product.name}</Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                >
                  {product.description || 'Produto preparado especialmente para voce'}
                </Typography>
              </Box>
            </Paper>
          ))}
        </Box>
      </Box>

      {items.length > 0 && (
        <Button
          variant="contained"
          color="success"
          onClick={() => setOrderDialogOpen(true)}
          sx={{
            position: 'fixed',
            left: 16,
            right: 16,
            bottom: 16,
            maxWidth: 868,
            mx: 'auto',
            py: 1.6,
            borderRadius: 3,
            fontSize: 17,
            fontWeight: 900,
            zIndex: 10
          }}
        >
          Pedir pedido ({totalQuantity}) - {money(total)}
        </Button>
      )}

      <Dialog open={Boolean(selectedProduct)} onClose={() => setSelectedProduct(null)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ p: 0 }}>
          <Box sx={{ height: 220, position: 'relative', bgcolor: '#e5e7eb' }}>
            {selectedProduct?.image && (
              <Box component="img" src={assetUrl(selectedProduct.image)} alt={selectedProduct.name} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            )}
            <IconButton onClick={() => setSelectedProduct(null)} sx={{ position: 'absolute', top: 12, right: 12, bgcolor: 'rgba(0,0,0,.65)', color: '#fff' }}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="h5" fontWeight={900}>{selectedProduct?.name}</Typography>
          {selectedProduct?.description && <Typography color="text.secondary" mt={0.5}>{selectedProduct.description}</Typography>}
          <Typography variant="h6" color="success.main" fontWeight={900} mb={2}>
            {money(displayedProductPrice)}
          </Typography>
          {getSizeOptions(selectedProduct).length > 0 && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 3 }}>
              <Typography fontWeight={900}>Escolha o tamanho</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${getSizeOptions(selectedProduct).length}, 1fr)`, gap: 1, mt: 1 }}>
                {getSizeOptions(selectedProduct).map((size) => (
                  <Button key={size.label} variant={selectedSize?.label === size.label ? 'contained' : 'outlined'} onClick={() => setSelectedSize(size)}>
                    {size.label} - {money(size.price)}
                  </Button>
                ))}
              </Box>
            </Paper>
          )}
          {isPizzaProduct && secondFlavorOptions.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 3, bgcolor: '#fff7ed' }}>
              <Typography fontWeight={900}>Como deseja sua pizza?</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mt: 1 }}>
                <Button
                  variant={pizzaMode === 'whole' ? 'contained' : 'outlined'}
                  onClick={() => { setPizzaMode('whole'); setSecondPizzaFlavor(null); }}
                >
                  Sabor inteiro
                </Button>
                <Button variant={pizzaMode === 'half' ? 'contained' : 'outlined'} onClick={() => setPizzaMode('half')}>
                  Metade/metade
                </Button>
              </Box>
              {pizzaMode === 'half' && (
                <Box mt={2}>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    Primeiro sabor: <strong>{selectedProduct?.name}</strong>. Escolha o segundo; sera cobrado o sabor mais caro.
                  </Typography>
                  <Box sx={{ display: 'grid', gap: 1, maxHeight: 260, overflowY: 'auto' }}>
                    {secondFlavorOptions.map((flavor) => (
                      <Paper
                        key={flavor.id}
                        variant="outlined"
                        onClick={() => setSecondPizzaFlavor(flavor)}
                        sx={{
                          p: 1.5,
                          cursor: 'pointer',
                          display: 'flex',
                          justifyContent: 'space-between',
                          gap: 2,
                          borderWidth: 2,
                          borderColor: secondPizzaFlavor?.id === flavor.id ? 'success.main' : 'divider',
                          bgcolor: secondPizzaFlavor?.id === flavor.id ? '#f0fdf4' : '#fff'
                        }}
                      >
                        <Typography fontWeight={800}>{flavor.name}</Typography>
                        <Typography fontWeight={900}>{money(flavor.price)}</Typography>
                      </Paper>
                    ))}
                  </Box>
                </Box>
              )}
            </Paper>
          )}
          {optionGroups.map((group) => (
            <Box key={group.id} mb={2}>
              <Typography fontWeight={900}>{group.name} {group.required ? '(obrigatorio)' : '(opcional)'}</Typography>
              {(group.options || []).map((option) => {
                const active = (selectedOptions[group.id] || []).some((entry) => Number(entry.id) === Number(option.id));
                return (
                  <Button
                    key={option.id}
                    fullWidth
                    variant={active ? 'contained' : 'outlined'}
                    onClick={() => toggleOption(group, option)}
                    sx={{ mt: 1, justifyContent: 'space-between' }}
                  >
                    <span>{option.name}</span>
                    <span>{Number(option.price) > 0 ? `+ ${money(option.price)}` : 'Incluido'}</span>
                  </Button>
                );
              })}
            </Box>
          ))}
          <TextField
            label="Observacao do produto"
            value={itemObservation}
            onChange={(event) => setItemObservation(event.target.value)}
            fullWidth
            multiline
            minRows={2}
            sx={{ mt: 1 }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mt: 2 }}>
            <IconButton onClick={() => setQuantity((value) => Math.max(1, value - 1))}><Remove /></IconButton>
            <Typography variant="h6" fontWeight={900}>{quantity}</Typography>
            <IconButton onClick={() => setQuantity((value) => value + 1)}><Add /></IconButton>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" fullWidth onClick={addItem}>Adicionar ao pedido</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={orderDialogOpen} onClose={() => !sending && setOrderDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Pedido da Mesa {mesa?.mesa_numero}</DialogTitle>
        <DialogContent dividers>
          {items.map((item) => (
            <Box key={item.lineId} sx={{ py: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                <Typography fontWeight={900}>{item.quantity}x {item.name}</Typography>
                <Typography fontWeight={900}>{money(item.unitPrice * item.quantity)}</Typography>
              </Box>
              {item.selectedOptions.length > 0 && (
                <Typography variant="body2" color="text.secondary">
                  {item.selectedOptions.map((option) => option.name).join(', ')}
                </Typography>
              )}
              {item.observation && <Typography variant="body2">Obs: {item.observation}</Typography>}
              <Button color="error" size="small" onClick={() => removeItem(item.lineId)}>Remover</Button>
              <Divider />
            </Box>
          ))}
          <Typography variant="h6" textAlign="right" fontWeight={900} my={2}>Total: {money(total)}</Typography>
          <TextField
            label="Seu nome"
            value={customerName}
            onChange={(event) => setCustomerName(event.target.value)}
            fullWidth
            required
            sx={{ mb: 2 }}
          />
          <TextField
            label="Observacao geral (opcional)"
            value={orderObservation}
            onChange={(event) => setOrderObservation(event.target.value)}
            fullWidth
            multiline
            minRows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOrderDialogOpen(false)} disabled={sending}>Voltar</Button>
          <Button variant="contained" color="success" onClick={submitOrder} disabled={sending || items.length === 0}>
            {sending ? 'Enviando...' : 'Pedir pedido'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(message)} autoHideDuration={3000} onClose={() => setMessage('')} message={message} />
    </Box>
  );
}
